export const MAX_FONT_FAMILY_NAME_LENGTH = 256

const LETTER_OR_NUMBER = /[\p{L}\p{N}]/u
const COMBINING_MARK = /\p{M}/u

/**
 * Derives a stable, CSS-safe display family from an uploaded filename.
 *
 * NFKC folds Arabic presentation forms back to their base letters. All
 * punctuation, separators, and format controls (including Persian ZWNJ) form
 * a single visible space, while letters/numbers from every script and their
 * combining marks are retained. This avoids both rejecting valid Persian
 * names and silently colliding `Peyda-Black` with `PeydaBlack`.
 */
export function fontFamilyNameFromFilename(filename: string): string | null {
  const extensionIndex = filename.lastIndexOf('.')
  const basename = (extensionIndex >= 0 ? filename.slice(0, extensionIndex) : filename).normalize('NFKC')
  let family = ''
  let tokenHasBase = false
  let separatorPending = false

  for (const character of basename) {
    if (LETTER_OR_NUMBER.test(character)) {
      if (separatorPending && family.length > 0) family += ' '
      family += character
      tokenHasBase = true
      separatorPending = false
      continue
    }
    if (COMBINING_MARK.test(character) && tokenHasBase) {
      family += character
      continue
    }
    if (family.length > 0) separatorPending = true
    tokenHasBase = false
  }

  family = family.normalize('NFC').trim()
  if (!LETTER_OR_NUMBER.test(family)) return null
  if (family.length <= MAX_FONT_FAMILY_NAME_LENGTH) return family

  let truncated = ''
  for (const character of family) {
    if (truncated.length + character.length > MAX_FONT_FAMILY_NAME_LENGTH) break
    truncated += character
  }
  truncated = truncated.trimEnd()
  return LETTER_OR_NUMBER.test(truncated) ? truncated : null
}
